import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      category: 'General',
      icon: 'HelpCircle',
      questions: [
        {
          question: 'What services does ProX11Market provide?',
          answer: `ProX11Market offers comprehensive football talent management and transfer facilitation services. We specialize in connecting elite African players with European opportunities through FIFA-compliant processes.\n\nOur services include:\n• Player representation and career management\n• Transfer negotiations and facilitation\n• Technical staff placement (coaches, analysts)\n• Performance analytics and scouting reports\n• Legal and documentation support\n• Career development programs\n• Market intelligence and insights`
        },
        {
          question: 'How long does it typically take to receive a response?',
          answer: `Our response times vary based on inquiry urgency:\n\n• Urgent inquiries: Within 4-6 hours\n• High priority: Within 24 hours\n• Medium priority: Within 48 hours\n• General inquiries: Within 72 hours\n\nAll responses come from specialized team members relevant to your inquiry type. For immediate assistance, we recommend using our live chat or phone consultation options.`
        },
        {
          question: 'Do you charge consultation fees?',
          answer: `Initial consultations are complimentary for all inquiry types. This includes:\n\n• 30-minute phone consultation\n• Video call introduction\n• Email inquiry response\n• Preliminary assessment\n\nDetailed service fees are discussed during consultation based on your specific needs. We offer transparent pricing with no hidden costs, and all fees are clearly outlined in our service agreements.`
        }
      ]
    },
    {
      category: 'For Clubs',
      icon: 'Building2',
      questions: [
        {
          question: 'How do you verify player credentials and performance data?',
          answer: `We maintain rigorous verification standards:\n\n• Direct partnerships with African football federations\n• Video analysis from official match footage\n• Medical assessments from certified facilities\n• Performance data from tracking systems\n• Background checks and documentation verification\n• Third-party validation of statistics\n\nAll player profiles include verification badges indicating the level of due diligence completed. We provide full transparency in our verification process.`
        },
        {
          question: 'What is your transfer success rate?',
          answer: `Our transfer completion statistics:\n\n• Overall success rate: 78% of initiated transfers completed\n• Average transfer timeline: 45-90 days\n• Client satisfaction: 94% positive feedback\n• Repeat client rate: 82%\n\nSuccess rates vary by league tier, player position, and transfer complexity. We provide realistic assessments during initial consultations and maintain transparent communication throughout the process.`
        }
      ]
    },
    {
      category: 'For Players',
      icon: 'User',
      questions: [
        {
          question: 'What are the requirements to be represented by ProX11Market?',
          answer: `Player representation criteria:\n\n• Age: 16-28 years (exceptions for exceptional talent)\n• Current level: Playing at professional or elite academy level\n• Performance: Demonstrated consistent high-level performance\n• Potential: Clear pathway to European football\n• Character: Professional attitude and commitment\n• Documentation: Valid passport and necessary paperwork\n\nWe conduct thorough assessments including video analysis, performance review, and personal interviews before offering representation.`
        },
        {
          question: 'How long does the European placement process take?',
          answer: `Timeline varies by individual circumstances:\n\n• Initial assessment: 1-2 weeks\n• Profile development: 2-4 weeks\n• Club outreach: 4-8 weeks\n• Trial arrangements: 2-4 weeks\n• Contract negotiations: 2-6 weeks\n• Documentation and visa: 4-8 weeks\n\nTotal timeline: 3-6 months on average. Exceptional cases may move faster, while complex situations may require additional time. We provide regular updates throughout the entire process.`
        }
      ]
    },
    {
      category: 'For Technical Staff',
      icon: 'Clipboard',
      questions: [
        {
          question: 'What coaching qualifications do you require?',
          answer: `Minimum qualification requirements:\n\n• UEFA B License or equivalent (minimum)\n• Relevant coaching experience (3+ years preferred)\n• Specialized certifications (advantage)\n• Language proficiency (English required)\n• Clean background check\n• Valid coaching insurance\n\nWe also consider:\n• Playing career achievements\n• Coaching philosophy and methodology\n• Cultural adaptability\n• Technology proficiency\n• References from previous positions`
        },
        {
          question: 'Do you provide visa and relocation support?',
          answer: `Comprehensive relocation assistance:\n\n• Work permit and visa application support\n• Documentation preparation and submission\n• Legal consultation for immigration matters\n• Housing assistance and recommendations\n• Cultural orientation and language support\n• Family relocation coordination\n• Banking and administrative setup\n\nWe partner with immigration specialists and relocation services to ensure smooth transitions for all technical staff placements.`
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const newIndex = categoryIndex * 100 + questionIndex;
    setOpenIndex(openIndex === newIndex ? -1 : newIndex);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="MessageSquareQuestion" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-primary">Common Questions</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-headline text-foreground mb-3 md:mb-4">
            Frequently Asked Questions
          </h2>
          
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about our services, processes, and how we can help you achieve your football career or business objectives.
          </p>
        </div>

        <div className="space-y-6 md:space-y-8">
          {faqs?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={category?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold font-headline text-foreground">
                  {category?.category}
                </h3>
              </div>

              <div className="space-y-3 md:space-y-4">
                {category?.questions?.map((faq, questionIndex) => {
                  const isOpen = openIndex === categoryIndex * 100 + questionIndex;
                  
                  return (
                    <div
                      key={questionIndex}
                      className="border border-border rounded-lg overflow-hidden transition-all duration-300"
                    >
                      <button
                        onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                        className="w-full flex items-center justify-between gap-4 p-4 md:p-5 text-left hover:bg-muted/50 transition-colors"
                      >
                        <span className="text-sm md:text-base font-medium text-foreground flex-1">
                          {faq?.question}
                        </span>
                        <Icon
                          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          className="text-muted-foreground flex-shrink-0 transition-transform duration-300"
                        />
                      </button>
                      {isOpen && (
                        <div className="px-4 md:px-5 pb-4 md:pb-5 animate-fade-in">
                          <div className="pt-3 md:pt-4 border-t border-border">
                            <p className="text-xs md:text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                              {faq?.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-12 text-center">
          <p className="text-sm md:text-base text-muted-foreground mb-4">
            Still have questions? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:contact@prox11market.com"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="Mail" size={18} />
              Email Us
            </a>
            <a
              href="tel:+442079460958"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-colors"
            >
              <Icon name="Phone" size={18} />
              Call Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;